import * as React from 'react';

import { Chip, ChipProps, Stack, TooltipProps } from '@mui/material';

type Icon = ChipProps['icon'];

type Tooltip = {
  Component: React.ComponentType<TooltipProps>;
  props: Omit<TooltipProps, 'children'>;
};

export type ChipOption = {
  value: string;
  label: string;
  disabled?: boolean;
  icon?: Icon;
  tooltip?: Tooltip;
};

type OptionProps = {
  label: string;
  value: string;
  selected: boolean;
  disabled?: boolean;
  icon?: Icon;
  tooltip?: Tooltip;
  onClick: (value: string) => void;
  style?: React.CSSProperties;
};

export type ChipOptionsProps = {
  name: string;
  defaultValue?: string;
  options: ChipOption[];
  onChange: (params: { name: string; value: string }) => void;
  optionStyle?: React.CSSProperties;
};

const Option: React.FC<OptionProps> = ({
  label,
  value,
  selected,
  icon,
  disabled,
  onClick,
  tooltip,
  style = {},
}) => {
  const renderChild = React.useCallback(
    () => (
      <Chip
        icon={icon}
        label={label}
        variant='outlined'
        color={disabled ? 'default' : 'primary'}
        onClick={() => (!disabled && !selected ? onClick(value) : undefined)}
        style={{
          padding: '18px 12px',
          borderRadius: '28px',
          fontSize: '14px',
          ...style,
        }}
        sx={
          selected
            ? { border: '1.5px solid', fontWeight: 600 }
            : { opacity: '0.5', border: '1px solid #DADADA' }
        }
      />
    ),
    [label, value, selected, icon, disabled, onClick, style],
  );

  return tooltip ? (
    <tooltip.Component {...tooltip.props}>{renderChild()}</tooltip.Component>
  ) : (
    renderChild()
  );
};

const ChipOptions: React.FC<ChipOptionsProps> = ({
  name,
  options,
  onChange,
  defaultValue = '',
  optionStyle = {},
}) => {
  const [value, setValue] = React.useState<string>();

  React.useEffect(() => {
    setValue(defaultValue);
  }, [defaultValue]);

  const handleClick = React.useCallback(
    (v: string) => {
      setValue(v);
      onChange({ name, value: v });
    },
    [name, setValue, onChange],
  );

  const selected = React.useCallback((v: string | undefined) => value === v, [value]);

  return (
    <Stack direction='row' spacing={1}>
      {options.map(({ value, ...restProps }) => (
        <Option
          {...restProps}
          key={value}
          value={value}
          selected={selected(value)}
          onClick={handleClick}
          style={{ ...optionStyle }}
        />
      ))}
    </Stack>
  );
};

export default ChipOptions;
